import React, { useState } from 'react';

/**
 * Who did what to a message from the dashboard: read, unread, moves, each with
 * the person and the time, newest first. Graph sees every such change as the
 * application's, so this record (the service's `mailbox_actions`) is the only
 * place a person's name is kept.
 *
 * Only changes made here are listed: something done in Outlook is not. Drawn
 * below the message body.
 */

const SHOWN = 3;

const when = (iso) =>
  iso
    ? new Date(iso).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : '';

/** One entry as a sentence. `folderName` turns a folder id into a name, or null. */
export const describeAction = (a, folderName) => {
  const name = (id) => (id && folderName(id)) || 'another folder';
  if (a.action === 'sent') return `Sent by ${a.actor}`;
  if (a.action === 'replied') return `Replied by ${a.actor}`;
  if (a.action === 'replied_all') return `Replied to all by ${a.actor}`;
  if (a.action === 'forwarded') return `Forwarded by ${a.actor}`;
  if (a.action === 'flagged') return `Flagged by ${a.actor}`;
  if (a.action === 'flag_complete') return `Flag marked complete by ${a.actor}`;
  if (a.action === 'unflagged') return `Flag cleared by ${a.actor}`;
  if (a.action === 'read') return `Marked read by ${a.actor}`;
  if (a.action === 'unread') return `Marked unread by ${a.actor}`;
  if (a.action === 'move') {
    return a.from_folder_id
      ? `Moved from ${name(a.from_folder_id)} to ${name(a.to_folder_id)} by ${a.actor}`
      : `Moved to ${name(a.to_folder_id)} by ${a.actor}`;
  }
  return `${a.action} by ${a.actor}`;
};

const Activity = ({ entries, folderName }) => {
  const [all, setAll] = useState(false);
  if (!entries || !entries.length) return null;
  const shown = all ? entries : entries.slice(0, SHOWN);
  return (
    <section className="mt-4 border-t border-gray-200 pt-3 text-sm">
      <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
        Activity
      </h3>
      <div className="text-gray-700">
        <ul className="space-y-0.5">
          {shown.map((a, i) => (
            <li key={`${a.at}-${i}`} className="break-words">
              {describeAction(a, folderName)}
              <span className="text-gray-400"> · {when(a.at)}</span>
            </li>
          ))}
        </ul>
        {entries.length > SHOWN && (
          <button
            type="button"
            onClick={() => setAll((x) => !x)}
            className="text-xs text-blue-700 hover:underline"
          >
            {all ? 'Show less' : `Show all ${entries.length}`}
          </button>
        )}
      </div>
    </section>
  );
};

export default Activity;
